import React from 'react';
import styled from 'styled-components';
import translate from '../../providers/i18n/translate';
import { ThemeContext } from '../../providers/theme';
import { useIntl } from 'react-intl';
import { TextField } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DateAdapter from '@mui/lab/AdapterMoment';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import SelectInput from '../inputs/SelectInput';
import { API_URL } from '../../config';
import axios from 'axios';
import BasicEditor from '../textEditors/basicEditor';
import { useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import * as actionCreators from "../../store/action-creators";
import { loadingConstants } from "../../store/reducers/loading/loadingConstants";

/* Styled components */
const Root = styled.form`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    height: auto;
    background-color: #FFF;
    border-radius: 8px;
    padding: 20px;
    max-height: 85vh;
    overflow-y: auto;
    @media (min-width: 1024px) {
        width: 700px;
    }
`

const Title = styled.h2`
    width: 100%;
    border-bottom: 1px solid ${props => props.borderColor ? props.borderColor : "#000"};
    text-transform: uppercase;
    color: ${props => props.color ? props.color : "#000"};
    margin: 0 0 10px 0;
`

const SectionTitle = styled.h3`
    width: 100%;
    color: ${props => props.color ? props.color : "#666"};
    margin: 20px 0 10px 0;
    font-size: 1.1em;
    border-bottom: 1px solid #eee;
    padding-bottom: 5px;
`

const Button = styled.button`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    padding: 5px;
    background-color: ${props => props.bg ? props.bg : "#7A7A7A"};
    color: ${props => props.color ? props.color : "#FFF"};
    border: none;
    outline: none;
    cursor: pointer;
    margin: 10px 0;
    transition: all 300ms ease-in-out;
    &:hover {
        transform: scale(1.1);
    }
`

const StyledTextfield = styled(TextField)`
    width: 100%;
    @media (min-width: 1024px) {
        width: ${props => props.width ? props.width : "inherit"};
    }
`

const InputGroup = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 10px;
    flex-direction: column;
    @media (min-width: 1024px) {
        flex-direction: row;
    }
`

const NewAppointmentWithIntervention = ({currentTab, userId, horseId, handleClose}) => {
    const theme = React.useContext(ThemeContext);
    const intl = useIntl();
    const [editorLoaded, setEditorLoaded] = React.useState(false);
    const dispatch = useDispatch();
    const {loadingAction, loadingProgress} = bindActionCreators(actionCreators, dispatch);

    const interventionTypeUrl = {
        farriery: `${API_URL}/interventionType/farriery`,
        general: `${API_URL}/interventionType/general`,
        health: `${API_URL}/interventionType/health`
    }
    
    const veterinarianUrl = {
        farriery: `${API_URL}/human/farrier`,
        general: `${API_URL}/human/veterinarian`,
        health: `${API_URL}/human/veterinarian`
    }
    
    const inputLabel = {
        farriery: "farrier",
        general: "veterinarian",
        health: "veterinarian"
    }
    
    const showVeterinarianAssistant = currentTab === "health" || currentTab === "general";

    const [formData, setFormData] = React.useState({
        name: '',
        info: '',
        date: new Date(),
        veterinarianId: null,
        veterinarianAssistantId: null,
        description: '',
        typeId: null
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    }

    const handleChangeAutocomplete = (name, value) => {
        setFormData({
            ...formData,
            [name]: value
        });
    }

    const handleChangeDate = (newValue) => {
        setFormData({
            ...formData,
            date: newValue
        });
    }

    const handleInfoEditor = (e, dataeditor) => {
        setFormData({
            ...formData,
            info: dataeditor
        })
    }

    const handleDescriptionEditor = (e, dataeditor) => {
        setFormData({
            ...formData,
            description: dataeditor
        })
    }

    const handleAction = (e) => {
        e.preventDefault();
        loadingAction(loadingConstants.WAIT);
        
        var formdata = new FormData();
        formdata.append("horseId", parseInt(horseId));
        formdata.append("name", formData.name);
        formdata.append("info", formData.info);
        formdata.append("date", formData.date);
        formdata.append("veterinarianId", formData.veterinarianId?.value);
        if (formData.veterinarianAssistantId?.value) {
            formdata.append("veterinarianAssistantId", formData.veterinarianAssistantId?.value);
        }
        formdata.append("description", formData.description);
        formdata.append("typeId", formData.typeId ? formData.typeId?.value : null);
        formdata.append("createdUserId", userId);

        const url = `${API_URL}/clinicHistory/appointments/intervention`;
        axios.post(url, formdata, {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: e => {
                const {loaded, total} = e;
                if (e.lengthComputable) {
                    let progress = loaded / total * 100;
                    loadingProgress(loadingConstants.PROGRESS, progress);
                }
            }
        })
        .then(response => {
            loadingAction(loadingConstants.READY);
            handleClose();
        })
        .catch(error => {
            loadingAction(loadingConstants.READY);
            alert(intl.formatMessage({ id: 'errorContact' }));
            console.log(error.response?.data?.msg);
        })
    }

    React.useEffect(() => {
        setEditorLoaded(true);
    }, [])

    return (
        <Root onSubmit={handleAction}>
            <Title>{translate("new appointment with intervention")}</Title>
            
            {/* Appointment*/}
            <SectionTitle>{translate("appointment details")}</SectionTitle>
            <TextField 
                id="name" 
                name="name" 
                onChange={handleChange} 
                value={formData.name} 
                label={intl.formatMessage({ id: 'issue' })} 
                variant="outlined" 
                fullWidth 
                margin="dense" 
                size="small" 
                required 
            />
            <LocalizationProvider dateAdapter={DateAdapter}>
                <DatePicker
                    renderInput={(props) => <StyledTextfield size="small" margin="dense" fullWidth {...props} />}
                    label={intl.formatMessage({ id: 'date' })}
                    value={formData.date}
                    onChange={handleChangeDate}
                    required
                />
            </LocalizationProvider>
            <InputGroup>
                <SelectInput 
                    value={formData.veterinarianId} 
                    fieldlabel="fullName" 
                    fieldvalue="id" 
                    dataurl={veterinarianUrl[currentTab]} 
                    label={intl.formatMessage({ id: inputLabel[currentTab] })} 
                    name="veterinarianId" 
                    onChange={handleChangeAutocomplete} 
                    width="45%" 
                    margin="dense" 
                    size="small" 
                    required 
                />
            {showVeterinarianAssistant && (
                <SelectInput 
                    value={formData.veterinarianAssistantId} 
                    fieldlabel="fullName" 
                    fieldvalue="id" 
                    dataurl={`${API_URL}/human/veterinarian`} 
                    label={intl.formatMessage({ id: 'veterinarianAssistant' })} 
                    name="veterinarianAssistantId" 
                    onChange={handleChangeAutocomplete} 
                    width="45%" 
                    margin="dense" 
                    size="small" 
                />
            )}
            </InputGroup>
            <BasicEditor
                name="info"
                value={formData.info}
                onChange={handleInfoEditor}
                editorLoaded={editorLoaded}
                maxContentHeight="100px"
                minContentHeight="100px"
                placeholder={intl.formatMessage({ id: 'insertDescription' })}
            />

            {/* Intervention*/}
            <SectionTitle>{translate("intervention details")}</SectionTitle>
            <SelectInput
                value={formData.typeId}
                fieldlabel="value"
                fieldvalue="id"
                dataurl={interventionTypeUrl[currentTab]}
                label={intl.formatMessage({ id: "interventionType" })}
                name="typeId"
                onChange={handleChangeAutocomplete}
                width="100%"
                margin="dense"
                size="small"
                required
            />
            <BasicEditor
                name="description"
                value={formData.description}
                onChange={handleDescriptionEditor}
                editorLoaded={editorLoaded}
                maxContentHeight="100px"
                minContentHeight="100px"
                placeholder={intl.formatMessage({ id: "insertDescription" })}
            />

            <Button bg={theme.palette.accent} color={theme.palette.base}>
                <CheckIcon />
            </Button>
        </Root>
    );
}

export default NewAppointmentWithIntervention; 